import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Link } from 'react-router-dom';
import { ArrowRight, Sun, TrendingUp, Leaf, Shield, Zap } from 'lucide-react';

const Home = () => {
  const benefits = [
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Save Money",
      description: "Reduce your electricity bills by up to 90% with solar energy."
    },
    {
      icon: <Leaf className="w-8 h-8" />,
      title: "Go Green",
      description: "Reduce your carbon footprint and help save the planet."
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Energy Independence",
      description: "Generate your own clean energy and protect against rising costs."
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Reliable Power",
      description: "Never worry about power outages with battery backup options."
    }
  ];

  const stats = [
    { value: "50+", label: "Happy Customers" },
    { value: "200KW", label: "Energy Generated" },
    { value: "1000+", label: "Tons CO₂ Saved" },
    { value: "100%", label: "Satisfaction Rate" }
  ];

  const brands = [
    { name: "Loom Solar", type: "Solar Panels", logo: "/solarpanel.png" },
    { name: "Adani Solar", type: "Solar Panels", logo: "/solarpanel.png" },
    { name: "Waaree", type: "Solar Panels", logo: "/solarpanel.png" },
    { name: "Luminous", type: "Inverter", logo: "/inverter.png" },
    { name: "Tata Power", type: "Solar Panels", logo: "/solarpanel.png" },
    { name: "Sungrow", type: "Inverter", logo: "/inverter.png" },
    { name: "Growatt", type: "Inverter", logo: "/inverter.png" },
    { name: "Panasonic", type: "Solar Panels", logo: "/solarpanel.png" },
    { name: "Avaada", type: "Solar Panels", logo: "/solarpanel.png" },
    { name: "Havells", type: "Solar Panels", logo: "/solarpanel.png" },
    { name: "Vikram Solar", type: "Solar Panels", logo: "/solarpanel.png" },
    { name: "Polycab", type: "Inverter", logo: "/inverter.png" }
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1591710369924-0dcd50436306?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxzb2xhciUyMHBhbmVscyUyMG1vZGVybiUyMGhvdXNlJTIwcm9vZiUyMHN1bm55fGVufDB8fHx8MTc3MTM5MjkzMHww&ixlib=rb-4.1.0&q=85')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black/60"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">

          {/* ✅ Fixed Badge - mobile friendly */}
          <div className="inline-flex items-center gap-2 bg-secondary/20 backdrop-blur-sm border border-secondary/30 rounded-full px-4 sm:px-6 py-2 sm:py-3 mb-8 max-w-[90vw]">
            <Sun className="w-4 h-4 sm:w-5 sm:h-5 text-secondary flex-shrink-0" />
            <span className="text-white font-medium text-sm sm:text-base">Leading Solar Installation Company</span>
          </div>

          {/* ✅ Fixed Heading - scales down on mobile */}
          <h1 className="font-heading font-bold text-4xl sm:text-5xl md:text-7xl text-white mb-6 tracking-tight leading-tight">
            Power Your Future with
            <span className="block text-secondary mt-2">Clean Solar Energy</span>
          </h1>

          {/* ✅ Fixed Subtext */}
          <p className="text-base sm:text-lg md:text-xl text-white/90 max-w-3xl mx-auto mb-10 sm:mb-12 leading-relaxed">
            Join thousands of homeowners and businesses making the switch to sustainable, cost-effective solar power. Start saving money and the planet today.
          </p>

          {/* ✅ Fixed Buttons - full width on mobile */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/calculator"
              data-testid="hero-calculator-button"
              className="w-full sm:w-auto bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-full px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg font-medium transition-all hover:scale-105 shadow-lg inline-flex items-center justify-center gap-2"
            >
              Calculate Solar Requirement <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/contact"
              data-testid="hero-contact-button"
              className="w-full sm:w-auto border-2 border-white text-white hover:bg-white/10 rounded-full px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg font-medium transition-all inline-flex items-center justify-center gap-2"
            >
              Get Free Quote
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-primary" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center" data-testid={`stat-${index}`}>
                <div className="font-heading font-bold text-4xl md:text-5xl text-secondary mb-2">{stat.value}</div>
                <div className="text-primary-foreground/80 text-sm md:text-base">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brand Logos Section */}
      <section className="py-16 border-b border-border/50" data-testid="brands-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-sm font-semibold uppercase tracking-widest text-muted-foreground mb-2">
              Trusted Brands We Install
            </p>
            <h2 className="font-heading font-semibold text-2xl md:text-3xl text-primary">
              Premium Solar Panels & Inverters
            </h2>
          </div>

          <div className="grid grid-cols-3 md:grid-cols-6 gap-6">
            {brands.map((brand, index) => (
              <div
                key={index}
                data-testid={`brand-${index}`}
                className="flex flex-col items-center justify-center gap-3 bg-card border border-border/50 rounded-2xl p-4 hover:border-primary/30 hover:shadow-md transition-all duration-300 group"
              >
                <div className="h-12 flex items-center justify-center">
                  <img
                    src={brand.logo}
                    alt={brand.name}
                    className="max-h-10 max-w-[90px] object-contain grayscale group-hover:grayscale-0 transition-all duration-300"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                  <span className="hidden font-heading font-bold text-sm text-primary">
                    {brand.name}
                  </span>
                </div>
                <div className="text-center">
                  <p className="font-medium text-xs text-foreground">{brand.name}</p>
                  <p className="text-xs text-muted-foreground">{brand.type}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-24" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading font-semibold text-3xl md:text-5xl text-primary mb-4 tracking-tight">
              Why Choose Solar Energy?
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Experience the benefits of clean, renewable energy for your home or business
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                data-testid={`benefit-card-${index}`}
                className="bg-card border border-border/50 shadow-sm hover:shadow-xl hover:border-primary/20 transition-all duration-300 rounded-3xl p-8 text-center group"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 text-primary rounded-2xl mb-6 group-hover:scale-110 transition-transform">
                  {benefit.icon}
                </div>
                <h3 className="font-heading font-semibold text-xl mb-3">{benefit.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-primary rounded-3xl p-8 sm:p-12 md:p-20 text-center relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-secondary/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-accent/20 rounded-full blur-3xl"></div>

            <div className="relative z-10">
              <h2 className="font-heading font-bold text-3xl md:text-5xl text-primary-foreground mb-6">
                Ready to Go Solar?
              </h2>
              <p className="text-primary-foreground/90 text-base sm:text-lg md:text-xl mb-8 max-w-2xl mx-auto leading-relaxed">
                Get a free consultation and discover how much you can save with solar energy
              </p>
              <Link
                to="/contact"
                data-testid="cta-contact-button"
                className="w-full sm:w-auto bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-full px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg font-medium transition-all hover:scale-105 shadow-lg inline-flex items-center justify-center gap-2"
              >
                Schedule Consultation <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
